import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { environment } from '@environments/environment';

// Tipo del API de Clarity sin importar el módulo en el bundle del servidor.
type ClarityApi = typeof import('@microsoft/clarity').default;
type ClarityTagValue = string | string[];

/**
 * Wrapper tipado sobre @microsoft/clarity.
 *
 * - Carga el SDK con import dinámico (solo en navegador) para no romper el SSR.
 * - No se activa en localhost ni sin `CLARITY_PROJECT_ID`.
 * - Todos los métodos son no-op mientras el SDK no esté cargado, así los llamadores
 *   nunca tienen que preocuparse del estado de inicialización.
 */
@Injectable({ providedIn: 'root' })
export class ClarityService {
    private readonly platformId = inject(PLATFORM_ID);
    private api: ClarityApi | null = null;
    private loading = false;

    // Hosts de desarrollo donde NO se debe cargar Clarity.
    private readonly BLOCKED_HOSTS = ['localhost', '127.0.0.1'];

    /** Arranca Clarity una sola vez. Idempotente y seguro de llamar varias veces. */
    async init(): Promise<void> {
        if (this.api || this.loading || !this.isEnabled()) return;

        this.loading = true;
        try {
            const mod = await import('@microsoft/clarity');
            mod.default.init(environment.CLARITY_PROJECT_ID);
            this.api = mod.default;
        } finally {
            this.loading = false;
        }
    }

    /** Etiqueta la sesión actual con una dimensión filtrable en el panel de Clarity. */
    setTag(key: string, value: ClarityTagValue): void {
        if (!this.api) return;
        if (value === '' || (Array.isArray(value) && value.length === 0)) return;
        this.api.setTag(key, value);
    }

    /** Registra un evento personalizado (sirve para armar embudos en Clarity). */
    event(name: string): void {
        if (!this.api) return;
        this.api.event(name);
    }

    /** Asocia la sesión a un huésped estable (id opaco, nunca PII legible). */
    identify(customId: string): void {
        if (!this.api || !customId) return;
        this.api.identify(customId);
    }

    private isEnabled(): boolean {
        if (!isPlatformBrowser(this.platformId)) return false;
        if (!environment.CLARITY_PROJECT_ID) return false;
        return !this.BLOCKED_HOSTS.includes(window.location.hostname);
    }
}
